// Ends a session the server has stopped accepting.
//
// The site decides you are signed in purely because a token is SITTING in
// localStorage — it never checks the token still works. Customer tokens last 30
// days and admin tokens 8 hours, and the server answers an expired one with a
// plain 401. Past the expiry the site went on showing a signed-in shopper while
// every authenticated call failed, and the callers mostly swallow that: an empty
// cart, saves that do nothing, and "sign out and back in" as the only cure.
//
// This is a fetch interceptor rather than a change to each caller because the
// token is attached by hand in ~50 files with no shared client. Patching the one
// function they all funnel through fixes every existing call site and every one
// written later, which a migration cannot promise.

type Handler = () => void;

const HANDLERS: Record<'customer' | 'admin', Handler | null> = { customer: null, admin: null };

export const TOKEN_KEYS = {
  customer: 'osw_customer_token',
  admin: 'osw_admin_token',
} as const;

export function setSessionExpiredHandler(kind: 'customer' | 'admin', fn: Handler | null) {
  HANDLERS[kind] = fn;
}

// One dead token means every panel on the page 401s at once. Only the first
// ends the session; the rest are the same event.
let endedAt = 0;
export function resetSessionGuard() { endedAt = 0; }

function endSession(kind: 'customer' | 'admin') {
  const now = Date.now();
  if (now - endedAt < 5000) return;
  endedAt = now;
  HANDLERS[kind]?.();
}

/** Pull the bearer token out of whatever shape the caller passed to fetch(). */
function sentToken(input: RequestInfo | URL, init?: RequestInit): string | null {
  const raw =
    new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
      .get('authorization');
  if (!raw) return null;
  const m = /^Bearer\s+(.+)$/i.exec(raw.trim());
  return m ? m[1] : null;
}

let installed = false;

/**
 * Wrap window.fetch once. Idempotent, so a double-invoked effect (React strict
 * mode) can't stack wrappers.
 */
export function installSessionGuard() {
  if (installed) return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input as any, init);

    // Only a 401 on a request that CARRIED a token says anything about the
    // session. A 401 from a public endpoint — wrong OTP, bad admin password —
    // must never sign anyone out, and those requests send no token.
    if (res.status === 401) {
      const token = sentToken(input, init);
      if (token) {
        // Which session died? Compare against what is stored rather than
        // guessing from the URL: admin and customer calls share paths.
        if (token === localStorage.getItem(TOKEN_KEYS.admin)) endSession('admin');
        else if (token === localStorage.getItem(TOKEN_KEYS.customer)) endSession('customer');
        // A token matching neither is already stale — the session that owned it
        // has been replaced, and there is nothing left to end.
      }
    }

    return res;
  };
}

/**
 * Is this stored token already past its expiry?
 *
 * Lets the app avoid restoring a session it can already tell is dead, instead of
 * coming up signed-in and dropping the user at their first click. The signature
 * is NOT checked — that stays the server's job, and the 401 path above is the
 * real backstop.
 *
 * Biased hard toward "not expired": anything unparseable keeps the session and
 * lets the server decide, because signing someone out by mistake is worse.
 */
export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const exp = JSON.parse(json)?.exp;
    if (typeof exp !== 'number') return false;
    return exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}
